import { ExternalLink, Info } from 'lucide-react'
import { useState } from 'react'

import { BANNER_COPY } from './utils'

import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface BannerPreviewProps {
  messageDe?: string
  messageEn?: string
  linkUrl?: string
}

type PreviewLanguage = 'de' | 'en'
type PreviewAudience = 'admin' | 'member'

/**
 * Approximation of the non-blocking banner rendered in the 360 portal
 * (epilot360-root-config). Only admins (`organization:edit`) see the acknowledge button.
 */
export const BannerPreview = ({
  messageDe,
  messageEn,
  linkUrl
}: BannerPreviewProps) => {
  const [language, setLanguage] = useState<PreviewLanguage>('de')
  const [audience, setAudience] = useState<PreviewAudience>('admin')

  const message =
    language === 'en' && messageEn?.trim()
      ? messageEn
      : messageDe?.trim()
      ? messageDe
      : ''

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Tabs
          onValueChange={(value) => setLanguage(value as PreviewLanguage)}
          value={language}
        >
          <TabsList>
            <TabsTrigger value="de">DE</TabsTrigger>
            <TabsTrigger value="en">EN</TabsTrigger>
          </TabsList>
        </Tabs>
        <Tabs
          onValueChange={(value) => setAudience(value as PreviewAudience)}
          value={audience}
        >
          <TabsList>
            <TabsTrigger value="admin">Org admin</TabsTrigger>
            <TabsTrigger value="member">Other member</TabsTrigger>
          </TabsList>
        </Tabs>
        {language === 'en' && !messageEn?.trim() && (
          <span className="text-xs text-muted-foreground">
            No EN message – English users see the DE text.
          </span>
        )}
      </div>
      <div
        className="flex flex-col gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950 md:flex-row md:items-center md:justify-between dark:border-blue-900 dark:bg-blue-950 dark:text-blue-50"
        data-testid="announcement-banner-preview"
        role="status"
      >
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-1">
            <p className="whitespace-pre-wrap">
              {message || (
                <span className="italic opacity-60">No message yet.</span>
              )}{' '}
              {linkUrl && (
                <a
                  className="inline-flex items-center gap-1 font-medium underline underline-offset-2"
                  href={linkUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  {BANNER_COPY.link}
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </p>
            {audience === 'member' && (
              <p className="text-xs opacity-80">{BANNER_COPY.nonAdminHint}</p>
            )}
          </div>
        </div>
        {audience === 'admin' && (
          <Button className="shrink-0" size="sm" type="button">
            {BANNER_COPY.acknowledgeButton}
          </Button>
        )}
      </div>
    </div>
  )
}
